// src/components/evenement/EventListPublic.js
import React, { useState, useEffect } from 'react';
import { 
    FaCalendarAlt,
    FaMapMarkerAlt,
    FaUsers,
    FaEuroSign,
    FaClock,
    FaSpinner,
    FaExclamationTriangle,
    FaSearch,
    FaFilter,
    FaImage,
    FaGraduationCap
} from 'react-icons/fa';
import './EventListPublic.css';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import FormulaireInscriptionCI from './FormulaireInscriptionCI';

// Importation des images locales
import flyerCIV from '../../assets/images/Flyer-RS-CIV (1).jpg';
import flyerCameroun from '../../assets/images/Flyer-RS-Cameroun.jpg';
import flyerForumCI from '../../assets/images/WhatsApp Image 2026-07-22 at 12.11.46.jpeg';

const EventListPublic = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [isFormOpen, setIsFormOpen] = useState(false);

    // Fonction pour calculer les inscriptions automatiques - A PARTIR D'AUJOURD'HUI
    const calculateAutoInscriptions = (startDate) => {
        if (!startDate) return 0;
        
        const now = new Date();
        // Date de référence : aujourd'hui
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        // Date de début des inscriptions : aujourd'hui
        const startInscriptions = new Date(today);
        
        // Calculer le nombre de jours écoulés depuis aujourd'hui
        const diffTime = now - startInscriptions;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        
        // Si on est avant aujourd'hui, retourner 0
        if (diffDays < 0) return 0;
        
        // 10 inscriptions par jour (jour 1 = 10, jour 2 = 20, etc.)
        // On ajoute +1 car on veut que le jour 1 = 10
        const daysPassed = diffDays + 1;
        const autoInscriptions = Math.min(daysPassed * 10, 200);
        
        return autoInscriptions;
    };

    // Données des événements manuels - LE NOUVEL ÉVÉNEMENT EN PREMIER
    const getManualEvents = () => {
        // Base des événements sans inscriptions
        const baseEvents = [
            {
                id: 3,
                slug: 'forum-preparation-depart-france-abidjan',
                title: 'Forum de Préparation au Départ en France',
                description: 'Préparez sereinement votre demande de visa étudiant et votre arrivée en France',
                shortDescription: 'Forum gratuit pour les étudiants souhaitant étudier en France',
                start_date: '2026-07-28T09:00:00',
                end_date: '2026-07-28T12:00:00',
                location: 'Lieu à renseigner, Abidjan, Côte d\'Ivoire',
                location_short: 'Abidjan, Côte d\'Ivoire',
                max_participants: 200,
                current_participants: 0,
                price: 0,
                meet_link: '',
                image: flyerForumCI,
                image_url: flyerForumCI,
                organizer: {
                    name: 'ETEF Côte d\'Ivoire',
                    email: 'etudieenfrance@gmail.com',
                    initials: 'ETEF'
                },
                status: 'upcoming',
                type: 'forum'
            },
            {
                id: 1,
                slug: 'salon-international-grandes-ecoles-abidjan',
                title: 'Salon international des Grandes écoles Françaises (SIGEF)',
                description: 'Orientation scolaire, poursuite d\'études en France et partenariat universitaire',
                shortDescription: 'Orientation scolaire, poursuite d\'études en France et partenariat universitaire',
                start_date: '2026-02-27T09:00:00',
                end_date: '2026-02-28T18:00:00',
                location: 'HOTEL PALM CLUB / ABIDJAN COCODY',
                location_short: 'HOTEL PALM CLUB / ABIDJAN COCODY',
                max_participants: null,
                current_participants: 0,
                price: 0,
                meet_link: '',
                image: flyerCIV,
                image_url: flyerCIV,
                organizer: {
                    name: 'NZEUKENG Kevin',
                    email: 'etudieenfrance@gmail.com',
                    initials: 'NK'
                },
                status: 'upcoming',
                type: 'salon'
            },
            {
                id: 2,
                slug: 'salon-international-grandes-ecoles-douala',
                title: 'Salon international des Grandes écoles Françaises (SIGEF)',
                description: 'Orientation scolaire, poursuite d\'études en France et partenariat universitaire',
                shortDescription: 'Orientation scolaire, poursuite d\'études en France et partenariat universitaire',
                start_date: '2026-03-13T18:00:00',
                end_date: '2026-03-14T18:00:00',
                location: 'HOTEL CRYSTAL PALACE / DOUALA',
                location_short: 'HOTEL CRYSTAL PALACE / DOUALA',
                max_participants: null,
                current_participants: 0,
                price: 0,
                meet_link: '',
                image: flyerCameroun,
                image_url: flyerCameroun,
                organizer: {
                    name: 'NZEUKENG Kevin',
                    email: 'etudieenfrance@gmail.com',
                    initials: 'NK'
                },
                status: 'upcoming',
                type: 'salon'
            }
        ];

        // Mettre à jour les inscriptions pour chaque événement
        return baseEvents.map(event => {
            if (event.type === 'forum') {
                const autoInscriptions = calculateAutoInscriptions(event.start_date);
                return {
                    ...event,
                    current_participants: autoInscriptions
                };
            }
            return event;
        });
    };

    useEffect(() => {
        const updatedEvents = getManualEvents();
        setEvents(updatedEvents);
        setFilteredEvents(updatedEvents);
        setLoading(false);
    }, []);

    // Effet pour mettre à jour les inscriptions toutes les minutes
    useEffect(() => {
        const interval = setInterval(() => {
            const updatedEvents = getManualEvents();
            setEvents(updatedEvents);
            setFilteredEvents(prev => {
                // Mettre à jour filteredEvents avec les nouvelles valeurs
                const searchLower = searchTerm.toLowerCase();
                if (!searchTerm.trim()) {
                    return updatedEvents;
                }
                const filtered = updatedEvents.filter(event => 
                    (event.title && event.title.toLowerCase().includes(searchLower)) ||
                    (event.description && event.description.toLowerCase().includes(searchLower)) ||
                    (event.location && event.location.toLowerCase().includes(searchLower))
                );
                return filtered;
            });
        }, 60000); // Mise à jour toutes les minutes

        return () => clearInterval(interval);
    }, [searchTerm]);

    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredEvents(events);
            return;
        }
        
        const searchLower = searchTerm.toLowerCase();
        const filtered = events.filter(event => 
            (event.title && event.title.toLowerCase().includes(searchLower)) ||
            (event.description && event.description.toLowerCase().includes(searchLower)) ||
            (event.location && event.location.toLowerCase().includes(searchLower))
        );
        
        setFilteredEvents(filtered);
    }, [searchTerm, events]);

    const formatDate = (dateString) => {
        try {
            if (!dateString) return 'Date non définie';
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Date invalide';
            return date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch (error) {
            return 'Date invalide';
        }
    };

    const getTimeRemaining = (startDate) => {
        try {
            if (!startDate) return 'Date non définie';
            
            const now = new Date();
            const start = new Date(startDate);
            
            if (isNaN(start.getTime())) return 'Date invalide';
            
            const diffTime = start - now;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            
            if (diffDays < 0) return 'Passé';
            if (diffDays === 0) return 'Aujourd\'hui';
            if (diffDays === 1) return 'Demain';
            if (diffDays < 7) return `Dans ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
            if (diffDays < 30) {
                const weeks = Math.floor(diffDays / 7);
                return `Dans ${weeks} semaine${weeks > 1 ? 's' : ''}`;
            }
            const months = Math.floor(diffDays / 30);
            return `Dans ${months} mois`;
        } catch (error) {
            return 'Date invalide';
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleViewEvent = (slug) => {
        if (slug) {
            window.open(`/events/${slug}`, '_blank');
        }
    };

    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        if (typeof imagePath === 'string' && (imagePath.startsWith('http://') || imagePath.startsWith('https://'))) {
            return imagePath;
        }
        return imagePath;
    };

    const handleOpenForm = () => {
        setIsFormOpen(true);
    };

    const handleCloseForm = () => {
        setIsFormOpen(false);
    };

    if (loading) {
        return (
            <div className="conteneur-principal">
                <Header />
                <div className="events-public-loading">
                    <FaSpinner className="spinner-icon" />
                    <p>Chargement des événements...</p>
                </div>
                <Footer />
            </div>
        );
    }

    if (error) {
        return (
            <div className="conteneur-principal">
                <Header />
                <div className="events-public-error">
                    <FaExclamationTriangle className="error-icon" />
                    <h3>Une erreur est survenue</h3>
                    <p>{error}</p>
                    <button onClick={() => {}} className="btn-retry">
                        Réessayer
                    </button>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="conteneur-principal">
            <Header />
            <div className="events-public-container">
                <div className="events-public-header">
                    <h2>
                        <FaCalendarAlt className="header-icon" />
                        Événements à Venir
                    </h2>
                    <p className="events-subtitle">
                        Rejoignez-nous à Abidjan pour un forum unique de préparation au départ en France
                    </p>
                    {/* BOUTON D'INSCRIPTION AU FORUM - VERT */}
                    <button 
                        className="btn-inscription-ci"
                        onClick={handleOpenForm}
                        style={{
                            marginTop: '20px',
                            padding: '14px 32px',
                            background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            fontSize: '1.1rem',
                            fontWeight: '700',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 8px 25px rgba(46, 204, 113, 0.4)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-3px)';
                            e.target.style.boxShadow = '0 12px 35px rgba(46, 204, 113, 0.5)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 8px 25px rgba(46, 204, 113, 0.4)';
                        }}
                    >
                        <FaGraduationCap />
                        Inscription gratuite - Réservez votre place maintenant !
                    </button>
                </div>

                <div className="events-search-section">
                    <div className="search-box">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Rechercher un événement par titre, description ou lieu..."
                            value={searchTerm}
                            onChange={handleSearch}
                            className="search-input"
                        />
                    </div>
                    {searchTerm && (
                        <div className="search-results-info">
                            {filteredEvents.length} événement(s) trouvé(s) pour "{searchTerm}"
                        </div>
                    )}
                </div>

                {filteredEvents.length === 0 ? (
                    <div className="no-events-public">
                        {searchTerm ? (
                            <>
                                <FaFilter className="no-events-icon" />
                                <h3>Aucun résultat trouvé</h3>
                                <p>Aucun événement ne correspond à votre recherche : "{searchTerm}"</p>
                                <button 
                                    onClick={() => setSearchTerm('')}
                                    className="btn-clear-search"
                                >
                                    Effacer la recherche
                                </button>
                            </>
                        ) : (
                            <>
                                <FaCalendarAlt className="no-events-icon" />
                                <h3>Aucun événement disponible</h3>
                                <p>Aucun événement n'est actuellement programmé. Revenez plus tard !</p>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="events-grid">
                        {filteredEvents.map((event) => {
                            const imageUrl = getImageUrl(event.image);
                            const dateDisplay = formatDate(event.start_date);
                            const isForum = event.type === 'forum';
                            
                            return (
                                <div key={event.id} className={`event-card ${isForum ? 'event-card-forum' : ''}`}>
                                    <div className="event-card-header">
                                        {imageUrl ? (
                                            <img 
                                                src={imageUrl}
                                                alt={event.title || 'Événement'}
                                                className="event-card-image"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.innerHTML += '<div class="no-image-icon"><svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 448 512" height="48" width="48" xmlns="http://www.w3.org/2000/svg"><path d="M152 64H296V24C296 10.75 306.7 0 320 0C333.3 0 344 10.75 344 24V64H384C419.3 64 448 92.65 448 128V448C448 483.3 419.3 512 384 512H64C28.65 512 0 483.3 0 448V128C0 92.65 28.65 64 64 64H104V24C104 10.75 114.7 0 128 0C141.3 0 152 10.75 152 24V64zM48 248H128L192 328L240 248L288 328L352 208L400 280V128C400 119.2 392.8 112 384 112H64C55.16 112 48 119.2 48 128V248z"></path></svg></div>';
                                                }}
                                            />
                                        ) : (
                                            <div className="no-image-icon">
                                                <FaImage />
                                            </div>
                                        )}
                                        <div className="event-time-badge" title={getTimeRemaining(event.start_date)}>
                                            <FaClock className="time-icon" />
                                            {getTimeRemaining(event.start_date)}
                                        </div>
                                        {isForum && (
                                            <div className="event-forum-badge">
                                                <FaGraduationCap />
                                                Forum
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div className="event-card-content">
                                        <h3 className="event-card-title" title={event.title}>
                                            {event.title || 'Événement sans titre'}
                                        </h3>
                                        
                                        <p className="event-card-description" title={event.description}>
                                            {event.description || 'Pas de description disponible'}
                                        </p>
                                        
                                        <div className="event-card-details">
                                            <div className="event-detail" title={dateDisplay}>
                                                <FaCalendarAlt className="detail-icon" />
                                                <span className="detail-text">
                                                    {dateDisplay}
                                                </span>
                                            </div>
                                            
                                            {event.location && (
                                                <div className="event-detail" title={event.location}>
                                                    <FaMapMarkerAlt className="detail-icon" />
                                                    <span className="detail-text location-text">
                                                        {event.location}
                                                    </span>
                                                </div>
                                            )}
                                            
                                            <div className="event-detail" title={`Participants: ${event.max_participants ? `${event.current_participants || 0}/${event.max_participants}` : 'Illimité'}`}>
                                                <FaUsers className="detail-icon" />
                                                <span className="detail-text">
                                                    {event.max_participants 
                                                        ? `${Math.min(event.current_participants || 0, event.max_participants)}/${event.max_participants}`
                                                        : 'Places illimitées'
                                                    }
                                                </span>
                                            </div>
                                            
                                            <div className="event-detail" title={event.price > 0 ? `${event.price} €` : 'Gratuit'}>
                                                <FaEuroSign className="detail-icon" />
                                                <span className="detail-text">
                                                    {event.price > 0 ? `${event.price} €` : 'Gratuit'}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <div className="event-card-actions">
                                            <button 
                                                onClick={() => handleViewEvent(event.slug)}
                                                className={`btn-view-details ${isForum ? 'btn-view-details-forum' : ''}`}
                                                style={isForum ? {
                                                    background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
                                                    boxShadow: '0 8px 25px rgba(46, 204, 113, 0.4)'
                                                } : {}}
                                                onMouseEnter={isForum ? (e) => {
                                                    e.target.style.transform = 'translateY(-3px)';
                                                    e.target.style.boxShadow = '0 12px 35px rgba(46, 204, 113, 0.5)';
                                                } : undefined}
                                                onMouseLeave={isForum ? (e) => {
                                                    e.target.style.transform = 'translateY(0)';
                                                    e.target.style.boxShadow = '0 8px 25px rgba(46, 204, 113, 0.4)';
                                                } : undefined}
                                            >
                                                Voir les détails
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <Footer />

            {/* Formulaire d'inscription Côte d'Ivoire */}
            {isFormOpen && (
                <FormulaireInscriptionCI 
                    onClose={handleCloseForm} 
                    eventName="Forum de Préparation au Départ en France"
                />
            )}
        </div>
    );
};

export default EventListPublic;